/**
 * Documentation Search
 *
 * Client-side search using Fuse.js with:
 * - Keyboard navigation (Cmd/Ctrl+K to open)
 * - Accessible modal dialog
 * - Fuzzy search across all doc content
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Fuse from 'fuse.js';

interface SearchResult {
  title: string;
  description: string;
  href: string;
  section: string;
}

// Search index - populated with all documentation pages
const searchIndex: SearchResult[] = [
  // Getting Started
  { title: 'Introduction', description: 'Welcome to ProgramRow documentation', href: '/docs', section: 'Getting Started' },
  { title: 'Quick Start', description: 'Get up and running with ProgramRow', href: '/docs/getting-started/quick-start', section: 'Getting Started' },
  { title: 'Core Concepts', description: 'Multi-tenancy, roles, and organization context', href: '/docs/getting-started/concepts', section: 'Getting Started' },

  // Org Owner Guides
  { title: 'Setup Organization', description: 'Configure your organization settings', href: '/docs/guides/org-owners/setup-organization', section: 'Org Owners' },
  { title: 'Manage Members', description: 'Invite and manage organization members', href: '/docs/guides/org-owners/manage-members', section: 'Org Owners' },
  { title: 'Billing', description: 'Manage subscription and billing', href: '/docs/guides/org-owners/billing', section: 'Org Owners' },

  // Admin Guides
  { title: 'Events', description: 'Create and manage events with roles and timelines', href: '/docs/guides/admins/events', section: 'Admin Guides' },
  { title: 'Crew Management', description: 'Add and manage crew members', href: '/docs/guides/admins/crew', section: 'Admin Guides' },
  { title: 'Assignments', description: 'Assign crew to events and track confirmations', href: '/docs/guides/admins/assignments', section: 'Admin Guides' },
  { title: 'Messaging', description: 'Send messages to crew via email and SMS', href: '/docs/guides/admins/messaging', section: 'Admin Guides' },
  { title: 'Documents', description: 'Upload and share documents with crew', href: '/docs/guides/admins/documents', section: 'Admin Guides' },
  { title: 'Pay Periods', description: 'Manage pay periods and track payments', href: '/docs/guides/admins/pay-periods', section: 'Admin Guides' },
  { title: 'Reports', description: 'Generate reports and export data', href: '/docs/guides/admins/reports', section: 'Admin Guides' },

  // API Reference
  { title: 'API Overview', description: 'Introduction to the ProgramRow API', href: '/docs/api', section: 'API Reference' },
  { title: 'Authentication', description: 'API authentication and tokens', href: '/docs/api/authentication', section: 'API Reference' },
  { title: 'Events API', description: 'Event endpoints and schemas', href: '/docs/api/events', section: 'API Reference' },
  { title: 'Assignments API', description: 'Assignment endpoints and schemas', href: '/docs/api/assignments', section: 'API Reference' },
  { title: 'Users API', description: 'User endpoints and schemas', href: '/docs/api/users', section: 'API Reference' },
  { title: 'Messages API', description: 'Message endpoints and schemas', href: '/docs/api/messages', section: 'API Reference' },
];

const fuse = new Fuse(searchIndex, {
  keys: ['title', 'description', 'section'],
  threshold: 0.3,
  includeScore: true,
});

export default function DocsSearch() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Keyboard shortcut to open search
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Search on query change
  useEffect(() => {
    if (query.trim()) {
      const searchResults = fuse.search(query).map((r) => r.item);
      setResults(searchResults.slice(0, 8));
      setSelectedIndex(0);
    } else {
      setResults([]);
    }
  }, [query]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setResults([]);
  }, []);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      navigate(result.href);
      handleClose();
    },
    [navigate, handleClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault();
        handleSelect(results[selectedIndex]);
      }
    },
    [results, selectedIndex, handleSelect]
  );

  return (
    <>
      {/* Search trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-rack"
        aria-label="Search documentation"
      >
        <SearchIcon className="w-4 h-4" />
        <span className="hidden sm:inline">Search</span>
        <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 text-xs font-medium text-gray-400 bg-gray-200 rounded">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>

      {/* Search modal */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-label="Search documentation"
        >
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm"
            onClick={handleClose}
            aria-hidden="true"
          />

          {/* Modal content */}
          <div className="relative min-h-screen flex items-start justify-center p-4 pt-[15vh]">
            <div className="relative w-full max-w-lg bg-white border border-hairline">
              {/* Search input */}
              <div className="flex items-center border-b border-gray-200">
                <SearchIcon className="w-5 h-5 ml-4 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search documentation..."
                  className="flex-1 px-4 py-4 text-base text-gray-900 placeholder-gray-400 bg-transparent border-0 focus:outline-none focus:ring-0"
                  aria-label="Search"
                  aria-autocomplete="list"
                  aria-controls="search-results"
                />
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-2 mr-2 text-gray-400 hover:text-gray-600"
                  aria-label="Close search"
                >
                  <kbd className="px-1.5 py-0.5 text-xs font-medium bg-gray-100 rounded">
                    Esc
                  </kbd>
                </button>
              </div>

              {/* Results */}
              {results.length > 0 && (
                <ul
                  id="search-results"
                  role="listbox"
                  className="max-h-80 overflow-y-auto py-2"
                >
                  {results.map((result, index) => (
                    <li
                      key={result.href}
                      role="option"
                      aria-selected={index === selectedIndex}
                    >
                      <button
                        type="button"
                        onClick={() => handleSelect(result)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        className={`w-full px-4 py-3 text-left transition-colors ${
                          index === selectedIndex
                            ? 'bg-primary-50'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <DocumentIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {result.title}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {result.section} &middot; {result.description}
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Empty state */}
              {query && results.length === 0 && (
                <div className="px-4 py-8 text-center text-gray-500">
                  <p className="text-sm">No results found for "{query}"</p>
                </div>
              )}

              {/* Help text */}
              {!query && (
                <div className="px-4 py-4 border-t border-gray-100">
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">↑</kbd>
                      <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">↓</kbd>
                      to navigate
                    </span>
                    <span className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">↵</kbd>
                      to select
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// =============================================================================
// Icons
// =============================================================================

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}
